import i18n from '../../../i18n';
import type { StateCreator } from 'zustand';
import { axiosInstance } from '../../utils/axiosApp';
import { LanguageCode } from '../../utils/utils';

export type NavigationTheme = 'light' | 'dark';

const NAVIGATION_THEME_STORAGE_KEY = 'portal.navigationTheme';
const I18NEXT_LANGUAGE_STORAGE_KEY = 'i18nextLng';

const readNavigationTheme = (): NavigationTheme => {
  if (typeof localStorage === 'undefined') return 'light';

  try {
    const stored = localStorage.getItem(NAVIGATION_THEME_STORAGE_KEY);
    return stored === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

const writeLanguagePreference = (language: LanguageCode): void => {
  try {
    localStorage.setItem(I18NEXT_LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Keep the in-memory preference even if browser storage is unavailable.
  }
};

export type AccountPreferences = {
  language: LanguageCode;
  navigationTheme: NavigationTheme;
};

export interface AccountPreferencesState {
  accountPreferences: AccountPreferences;
  accountPreferencesLoading: boolean;
  accountPreferencesError: string | null;

  fetchAccountPreferences: () => Promise<void>;
  patchAccountLanguage: (language: LanguageCode) => Promise<void>;
  setNavigationTheme: (theme: NavigationTheme) => void;
}

export const createAccountPreferencesSlice: StateCreator<
  AccountPreferencesState,
  [],
  [],
  AccountPreferencesState
> = (set, get) => ({
  accountPreferences: {
    language: (i18n.language as LanguageCode) ?? 'en',
    navigationTheme: readNavigationTheme(),
  },
  accountPreferencesLoading: false,
  accountPreferencesError: null,

  fetchAccountPreferences: async () => {
    set({ accountPreferencesLoading: true, accountPreferencesError: null });
    try {
      const res = await axiosInstance().get('/api/me/preferences');

      const lang = (res.data?.language ??
        (i18n.language as LanguageCode) ??
        'en') as LanguageCode;

      await i18n.changeLanguage(lang);
      writeLanguagePreference(lang);

      set({
        accountPreferences: {
          ...get().accountPreferences,
          language: lang,
          navigationTheme: readNavigationTheme(),
        },
        accountPreferencesLoading: false,
      });
    } catch (e) {
      set({
        accountPreferencesLoading: false,
        accountPreferencesError: 'Failed to fetch account preferences',
      });
      throw e;
    }
  },

  patchAccountLanguage: async (language: LanguageCode) => {
    if (language === get().accountPreferences.language) return;

    set({ accountPreferencesError: null });

    await axiosInstance().patch(
      '/api/me/preferences',
      { language },
      { timeout: 30_000 }
    );

    await i18n.changeLanguage(language);
    writeLanguagePreference(language);

    set({
      accountPreferences: {
        ...get().accountPreferences,
        language,
        navigationTheme:
          get().accountPreferences.navigationTheme ?? readNavigationTheme(),
      },
    });
  },

  setNavigationTheme: (navigationTheme: NavigationTheme) => {
    try {
      localStorage.setItem(NAVIGATION_THEME_STORAGE_KEY, navigationTheme);
    } catch {
      // Keep the in-memory preference even if browser storage is unavailable.
    }
    set({
      accountPreferences: {
        ...get().accountPreferences,
        navigationTheme,
      },
    });
  },
});
